import { ChangeDetectionStrategy, Component, Inject, Input, OnInit } from '@angular/core';

import { OddsDisplayFormat } from '@frontend/sports/types/models/user-settings';
import { Store } from '@ngrx/store';
import { Observable, map } from 'rxjs';

import { FeatureLocation, IARCProfileConfiguration } from '../../../lib/arc/arc.model';
import { BetslipType } from '../../core/betslip-type';
import { LinearTypes } from '../../core/linear-types';
import { OverAskFlowState } from '../../model/over-ask/over-ask';
import {
    PLACE_BUTTON_MESSAGES_PROVIDER,
    PlaceButtonMessagesProviderService,
} from '../../modules/betplacement/services/place-button/place-button-messages-provider.service';
import {
    IPlaceButtonStateService,
    PLACE_BUTTON_ACTION_HANDLER_SERVICE,
    PLACE_BUTTON_ACTION_SERVICE,
    PLACE_BUTTON_DISABLE_SERVICE,
    PLACE_BUTTON_MESSAGE_SERVICE,
    PLACE_BUTTON_STATE_SERVICE,
    PLACE_BUTTON_TITLE_SERVICE,
    PLACE_BUTTON_VIEWMODEL_SERVICE,
} from '../../modules/betplacement/services/place-button/viewmodel/interfaces/place-button-interfaces';
import { LinearPlaceButtonActionHandlerService } from '../../modules/betplacement/services/place-button/viewmodel/linear-betslip/linear-place-button-action-handler.service';
import { LinearPlaceButtonMessageService } from '../../modules/betplacement/services/place-button/viewmodel/linear-betslip/linear-place-button-message.service';
import { LinearPlaceButtonStateService } from '../../modules/betplacement/services/place-button/viewmodel/linear-betslip/linear-place-button-state.service';
import { BetslipPlaceButtonActionService } from '../../modules/betplacement/services/place-button/viewmodel/regular-betslip/betslip-place-button-action.service';
import { BetslipPlaceButtonDisableService } from '../../modules/betplacement/services/place-button/viewmodel/regular-betslip/betslip-place-button-disable.service';
import { BetslipPlaceButtonTitleService } from '../../modules/betplacement/services/place-button/viewmodel/regular-betslip/betslip-place-button-title.service';
import { BetslipPlaceButtonViewModelService } from '../../modules/betplacement/services/place-button/viewmodel/regular-betslip/betslip-place-button-viewmodel.service';
import { BetslipOnboardingMessageService } from '../../modules/onboarding/betslip-onboarding-message.service';
import { overAskFlowStateSelector } from '../../modules/over-ask/selectors';
import { QuickDepositSource } from '../../modules/quick-deposit/quick-deposit.models';
import { selectQuickDepositIsOpen } from '../../modules/quick-deposit/selector';
import { settingsOpenSelector } from '../../modules/settings/digital-selectors';
import LinearBetslipTrackingActions from '../../modules/tracking/state/linear-tracking.actions';
import { betslipTypesBaseSelector } from '../../modules/types/base/selectors';

@Component({
    selector: 'bs-betslip-linear-edit-state',
    templateUrl: './betslip-linear-edit-state.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: [
        './betslip-edit-state.component.scss',
        './betslip-linear-edit-state.component.scss',
        './betslip-linear-edit-state.component.theme.scss',
    ],
    providers: [
        { provide: PLACE_BUTTON_STATE_SERVICE, useClass: LinearPlaceButtonStateService },
        { provide: PLACE_BUTTON_ACTION_HANDLER_SERVICE, useClass: LinearPlaceButtonActionHandlerService },
        { provide: PLACE_BUTTON_VIEWMODEL_SERVICE, useClass: BetslipPlaceButtonViewModelService },
        { provide: PLACE_BUTTON_ACTION_SERVICE, useClass: BetslipPlaceButtonActionService },
        { provide: PLACE_BUTTON_MESSAGE_SERVICE, useClass: LinearPlaceButtonMessageService },
        { provide: PLACE_BUTTON_TITLE_SERVICE, useClass: BetslipPlaceButtonTitleService },
        { provide: PLACE_BUTTON_DISABLE_SERVICE, useClass: BetslipPlaceButtonDisableService },
        { provide: PLACE_BUTTON_MESSAGES_PROVIDER, useClass: PlaceButtonMessagesProviderService },
    ],
    standalone: false,
})
export class BetslipLinearEditStateComponent implements OnInit {
    @Input() oddsDisplayFormat: OddsDisplayFormat;
    @Input() inColumn = false;
    @Input() isUserOnPlayBreak = false;
    @Input() profileData$: Observable<IARCProfileConfiguration>;
    @Input() hasArcPreBetMessage$: Observable<boolean>;

    scrollerMinHeight: string;
    featureLocation = FeatureLocation;

    readonly isQuickDepositOpen$: Observable<boolean>;
    readonly isButtonProcessing$: Observable<boolean>;

    readonly betslipTypes = BetslipType;
    readonly tabProps$: Observable<{
        tabProps: {
            selectedBetslipType: BetslipType | null;
            activeBetslipTypes: BetslipType[];
            linearTypes: LinearTypes;
        };
    }>;

    readonly isSettingsOpened$: Observable<boolean>;
    readonly hasOnboarding$: Observable<boolean>;
    readonly isOveraskOpened$: Observable<boolean>;
    readonly quickDepositSource = QuickDepositSource.Betslip;

    constructor(
        private store: Store,
        betslipOnboardingMessageService: BetslipOnboardingMessageService,
        @Inject(PLACE_BUTTON_STATE_SERVICE) placeButtonService: IPlaceButtonStateService,
    ) {
        this.tabProps$ = store.select(betslipTypesBaseSelector).pipe(
            map((state) => ({
                tabProps: {
                    selectedBetslipType: state.currentSelectedType,
                    activeBetslipTypes: state.activeTypes,
                    linearTypes: state.linearTypes,
                },
            })),
        );

        this.isSettingsOpened$ = store.select(settingsOpenSelector);
        this.hasOnboarding$ = betslipOnboardingMessageService.onboardingMessage$.pipe(map((onboardingMessage) => !!onboardingMessage));
        this.isButtonProcessing$ = placeButtonService.state$.pipe(map((state) => state.isProcessing));
        this.isQuickDepositOpen$ = store.select(selectQuickDepositIsOpen);
        this.isOveraskOpened$ = store.select(overAskFlowStateSelector).pipe(map((flowState) => flowState !== OverAskFlowState.None));
    }

    ngOnInit(): void {
        this.store.dispatch(LinearBetslipTrackingActions.loadBetslip());
    }

    onSettingsOpened($event: number) {
        this.scrollerMinHeight = $event + 'px';
    }
}
